import {
  Integration,
  RawIntegrationBanner,
  PipelineReplicationTypeMode,
  RawIntegration,
  ReplicationTypeOption,
  IntegrationAction,
  IntegrationBannerAction
} from '../../shared/interfaces/integration';
import { SourceFactory } from '../../sources/SourceList/factories/sourceFactory';
import { DestinationFactory } from '../../destinations/DestinationList/factories/destinationFactory';
import { PIPELINE_STATUS_DATA_MAP } from '../constants/pipeline-status';
import {
  EntityDisplayConfig,
  RawEntityDisplayConfig
} from '../../shared/interfaces/entity-display-config';
import { EntityDisplayConfigFactory } from '../../shared/factory/entityDisplayConfigFactory';
import { RawSourceType } from '../../shared/interfaces/source';
import { RawDestinationType } from '../../shared/interfaces/destination';
import { EntityMetaFactory } from '../../shared/factory/entityMetaFactory';
import {
  CommonOption,
  SyncType,
  SyncTypeFeatureOption
} from '../interfaces/pipeline-settings-config';
import { PIPELINE_ACTIONS_DISPLAY_DETAILS } from '../PipelineActions/constants/pipeline-actions';
import { EntityActionsFactory } from '../../entity-actions/factory/entityActionsFactory';
import { EntityBannersFactory } from '../../shared/components/EntityBanner/factory/entityBannersFactory';

export function IntegrationsFactory(rawIntegrations: RawIntegration[], sort = true): Integration[] {
  if (!rawIntegrations) {
    return [];
  }

  const pipelines = rawIntegrations.map(IntegrationFactory);

  if (!sort) {
    return pipelines;
  }

  return pipelines.sort((p1, p2) => p2.id - p1.id);
}

export function IntegrationFactory(rawIntegration: RawIntegration): Integration {
  return {
    id: rawIntegration.id,
    seqId: rawIntegration.seq_id,
    name: rawIntegration.name || '',
    sourceId: rawIntegration.source_id || null,
    destinationId: rawIntegration.destination_id || null,
    destinationPrefix: rawIntegration.destination_prefix || null,
    source: rawIntegration.source ? SourceFactory(rawIntegration.source) : null,
    destination: rawIntegration.destination ? DestinationFactory(rawIntegration.destination) : null,
    config: rawIntegration.config || {},
    metadata: rawIntegration.metadata || {},
    status: PipelineStatusFactory(rawIntegration.status),
    actions: EntityActionsFactory<IntegrationAction>(
      rawIntegration.integration_actions ?? [],
      PIPELINE_ACTIONS_DISPLAY_DETAILS
    ),
    message: {
      ...(rawIntegration.message || {})
    },
    ...EntityMetaFactory({
      created_ts: rawIntegration.created_ts,
      created_by: rawIntegration.created_by,
      updated_by: rawIntegration.updated_by,
      updated_ts: rawIntegration.updated_ts
    })
  };
}

export function PipelineStatusFactory(rawStatus: RawIntegration['status']) {
  return {
    ...PIPELINE_STATUS_DATA_MAP[rawStatus.type],
    helperText: rawStatus.hover_text || undefined
  };
}

export function IntegrationDisplayConfigFactory(rawDetails: {
  source: RawEntityDisplayConfig<RawSourceType>;
  destination: RawEntityDisplayConfig<RawDestinationType>;
  integration: RawEntityDisplayConfig<RawIntegration>;
}): {
  sourceDisplayConfig: EntityDisplayConfig;
  destinationDisplayConfig: EntityDisplayConfig;
  integrationDisplayConfig: EntityDisplayConfig;
} {
  return {
    sourceDisplayConfig: EntityDisplayConfigFactory<RawSourceType>(rawDetails.source),
    destinationDisplayConfig: EntityDisplayConfigFactory<RawDestinationType>(
      rawDetails.destination
    ),
    integrationDisplayConfig: EntityDisplayConfigFactory<RawIntegration>(rawDetails.integration)
  };
}

export function IntegrationBannerFactory(rawBanners: RawIntegrationBanner[]) {
  return EntityBannersFactory<IntegrationBannerAction>(rawBanners);
}

function createCommonTypeFactory<T extends CommonOption, D>(options: T[], defaultOption: D) {
  return options.map(option => ({
    id: option.value,
    label: option.label,
    value: option.value,
    description: option.helper_text.text,
    isRecommended: option.value === defaultOption
  }));
}

export function IntegrationReplicationTypesFactory(
  options: ReplicationTypeOption[],
  defaultOption: PipelineReplicationTypeMode
) {
  return createCommonTypeFactory(options, defaultOption);
}

export function IntegrationSyncTypesFactory(
  options: SyncTypeFeatureOption[],
  defaultOption: SyncType
) {
  return createCommonTypeFactory(options, defaultOption);
}
